import React from 'react';
import type { DashboardModel } from '../../hooks/useDashboard';
import DashboardEventsTab from './DashboardEventsTab';
import DashboardHistoryTab from './DashboardHistoryTab';

const DashboardDetailTray: React.FC<DashboardModel> = (model) => {
  const {
    dark,
    selectedAircraft,
    activeTab,
    historyDateFrom,
    setSelectedAircraft,
    setActiveTab,
    setHistoryDateFrom,
    setHistoryDateTo,
  } = model;

  if (!selectedAircraft) return null;

  const tabs = [
    { id: 'events', label: 'Recent Events' },
    { id: 'history', label: 'History' },
    { id: 'calculations', label: 'Calculations' },
  ] as const;

  return (
    <section
      className={`h-full flex flex-col overflow-hidden rounded-[2rem] border shadow-2xl ${
        dark
          ? 'bg-[linear-gradient(160deg,rgba(15,23,42,0.97),rgba(2,6,23,0.93))] border-blue-300/20 shadow-[0_28px_90px_rgba(2,8,24,0.72),inset_0_1px_0_rgba(125,211,252,0.16)]'
          : 'bg-white border-slate-200/90 shadow-[0_24px_70px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,0.95)]'
      }`}
    >
      {/* Header: tabs + close */}
      <header
        className={`h-12 border-b flex items-center justify-between px-4 shrink-0 ${
          dark ? 'bg-slate-950/80 border-slate-700/60' : 'bg-white border-slate-200'
        }`}
      >
        <div className="flex items-center gap-1 min-w-0">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id as any);
                  if (tab.id === 'history' && !historyDateFrom) {
                    const to = new Date();
                    const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
                    setHistoryDateFrom(from.toISOString().slice(0, 16));
                    setHistoryDateTo(to.toISOString().slice(0, 16));
                  }
                }}
                className={`relative px-2.5 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-[0.1em] transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/30'
                    : dark
                      ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => setSelectedAircraft(null)}
          className={`ml-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all ${
            dark
              ? 'text-slate-500 hover:text-white hover:bg-slate-800'
              : 'text-slate-400 hover:text-slate-800 hover:bg-slate-100'
          }`}
          title="Close"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      {/* Aircraft identity strip */}
      <div
        className={`flex items-center gap-3 px-4 py-2.5 shrink-0 border-b ${
          dark ? 'bg-slate-900/70 border-slate-700/60' : 'bg-white border-slate-100'
        }`}
      >
        <div
          className={`h-11 w-[68px] rounded-xl overflow-hidden shrink-0 border ${
            dark ? 'bg-slate-700 border-slate-600' : 'bg-slate-100 border-slate-200'
          }`}
        >
          <img
            src={selectedAircraft.imageUrl || 'https://via.placeholder.com/300x150'}
            className="w-full h-full object-cover"
            alt="Aircraft"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className={`text-[13px] font-black font-mono tracking-tight leading-none ${
                dark ? 'text-amber-300' : 'text-slate-900'
              }`}
            >
              {selectedAircraft.tailNumber}
            </span>
            <span className={`flex items-center gap-1 text-[7px] font-black uppercase tracking-[0.1em] ${
              dark ? 'text-emerald-400' : 'text-emerald-600'
            }`}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              Live
            </span>
          </div>
          <p
            className={`text-[9px] font-medium truncate ${
              dark ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            {selectedAircraft.name}
          </p>
        </div>
      </div>

      {/* Full-width tab content */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {activeTab === 'events' && <DashboardEventsTab {...model} />}
        {activeTab === 'history' && <DashboardHistoryTab {...model} />}
        {activeTab === 'calculations' && (
          <div
            className={`flex-1 flex items-center justify-center ${
              dark ? 'bg-slate-900/60' : 'bg-slate-50'
            }`}
          >
            <p
              className={`text-[10px] font-bold uppercase tracking-widest ${
                dark ? 'text-slate-500' : 'text-slate-400'
              }`}
            >
              Calculations — Coming Soon
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer
        className={`h-6 px-5 flex items-center justify-between shrink-0 text-[7px] font-black uppercase tracking-widest ${
          dark
            ? 'bg-slate-950 text-slate-600'
            : 'bg-slate-50 text-slate-400 border-t border-slate-200'
        }`}
      >
        <div className="flex gap-4">
          <span>Terms &amp; Conditions</span>
          <span>Privacy Policy</span>
        </div>
        <span>Miracle Air Operations Command Center</span>
      </footer>
    </section>
  );
};

export default DashboardDetailTray;
